import { Fragment, useCallback, useContext, useEffect, useState } from 'react'
import { Listbox, Transition } from '@headlessui/react'
import { RxChevronDown } from 'react-icons/rx'
import { getCompetitionImage } from '../utils/competition'
import { AppContext } from '../contexts/AppContext'
import { MdOutlineTableRows } from 'react-icons/md'
import { IoIosFootball } from 'react-icons/io'

const CompetitionSelector = ({
  value,
  onChange,
  competitions,
  defaultOption = {
    text: 'Select Competitions',
    value: '',
    enabled: true,
  },
  className = 'min-w-[300px]', // Allow custom width
}) => {
  // Auto-select the competition flagged as default (falling back to the first
  // one) whenever nothing is selected yet, so it always shows pre-selected.
  useEffect(() => {
    if (value || !competitions?.length) return
    const defaultCompetition = competitions.find((comp) => comp.isDefault) || competitions[0]
    onChange(defaultCompetition)
  }, [value, competitions, onChange])

  return (
    <Listbox
      value={value}
      onChange={onChange}
    >
      <div className={`relative ${className}`}>
        <Listbox.Button className='form-control relative w-full cursor-pointer rounded-lg bg-white py-2 pl-4 pr-10 text-left border border-gray-200 hover:bg-gray-100'>
          <div className='flex gap-2 items-center justify-start'>
            {value?.logo && (
              <img
                width='30'
                height='30'
                src={getCompetitionImage(value.logo)}
                alt={value.name}
                className='rounded border-2 border-neutral-100'
              />
            )}
            <span className='block text-sm font-medium tracking-wider'>
              {value ? value.name : 'Select competitions'}
            </span>
          </div>
          <span className='pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2'>
            <RxChevronDown
              className='h-5 w-5 text-gray-400'
              aria-hidden='true'
            />
          </span>
        </Listbox.Button>

        <Transition
          as={Fragment}
          leave='transition ease-in duration-100'
          leaveFrom='opacity-100'
          leaveTo='opacity-0'
        >
          <Listbox.Options className='absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10'>
            {defaultOption.enabled && (
              <Listbox.Option
                value={defaultOption.value}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 px-4 font-medium ${
                    active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                  }`
                }
              >
                <div className='flex justify-start gap-4 text-base font-medium items-center text-neutral-800'>
                  <IoIosFootball className='w-[30px] h-[30px]' />
                  {defaultOption.text}
                </div>
              </Listbox.Option>
            )}
            {competitions.map((comp) => (
              <Listbox.Option
                key={comp.id}
                value={comp}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 px-4 ${
                    active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                  }`
                }
              >
                <div className='flex justify-start gap-4 text-base font-medium items-center text-neutral-800'>
                  {comp.logo && (
                    <img
                      width='30'
                      height='30'
                      src={getCompetitionImage(comp.logo)}
                      alt={comp.name}
                      className='rounded border-2 border-neutral-100'
                    />
                  )}
                  {comp.name}
                </div>
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

export default CompetitionSelector
